import { Banknote, Landmark } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { cajaBase, esCajaN } from '@/shared/lib/cajasEfectivo';

/**
 * Nombre de caja para pantalla. La gemela sin factura ("Efectivo Pesos N" en
 * la base, ver lib/cajasEfectivo) se muestra con el nombre de su caja base y
 * el ÍCONO EN ROJO: el cliente no quiere ver la "N". La que lleva factura se
 * distingue por la etiqueta verde (`LlevaFacturaTag`), que va aparte.
 *
 * `soloMarca`: para tablas donde hoy no hay íconos (Administración,
 * Compras) — el ícono aparece únicamente en la caja sin factura.
 */
export function CajaNombre({
  caja,
  tipo,
  soloMarca = false,
  className,
}: {
  caja: string | null | undefined;
  /** `Tipo_MDP`, respaldo para elegir el ícono (ver `CajaIcon`). */
  tipo?: string | null;
  soloMarca?: boolean;
  className?: string;
}) {
  const nombre = (caja ?? '').trim();
  if (!nombre) return <>—</>;
  const sinFactura = esCajaN(nombre);
  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-2', className)}
      title={sinFactura ? `${cajaBase(nombre)} · sin factura` : undefined}
    >
      {(!soloMarca || sinFactura) && (
        <CajaIcon caja={nombre} tipo={tipo ?? ''} sinFactura={sinFactura} />
      )}
      <span className="truncate">{cajaBase(nombre)}</span>
      {sinFactura && <span className="sr-only">{' '}(sin factura)</span>}
    </span>
  );
}

/**
 * Banco (edificio) para las transferencias bancarias, billete para el resto.
 *
 * NO se puede confiar sólo en `Tipo_MDP`: en AR está cargado al revés —
 * Transferencia Galicia / Credicoop / MercadoPago figuran como 'Efectivo' y
 * Efectivo Pesos / Dolar / Morba lo tienen vacío (verificado 2026-09-04).
 * Por eso manda el nombre y `Tipo_MDP` queda como respaldo, que es el que
 * cubre a MX (BBVA Italmexy, Nova Banorte), donde sí está bien cargado.
 */
function CajaIcon({
  caja,
  tipo,
  sinFactura,
}: {
  caja: string;
  tipo: string;
  sinFactura: boolean;
}) {
  const cls = cn('h-4 w-4 shrink-0', sinFactura ? 'text-rose-600' : 'text-muted-foreground');
  const esBanco = /^transferencia\b/i.test(caja) || tipo === 'Banco';
  return esBanco ? <Landmark className={cls} aria-hidden /> : <Banknote className={cls} aria-hidden />;
}
